import React, { useCallback, useEffect, useState } from 'react';

import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import AccountLayout from 'components/layout/account';
import LoadingAnimation from 'components/loading-animation';
import ModalOrderUnavailable from 'components/modal-order-unavailable';
import { BackArrowHistory } from 'components/navigation/back-arrow';
import { RecentItems } from 'components/recent-items';
import {
  RefreshButton,
  RefreshButtonContainer,
  StyledRefreshIcon,
} from 'components/refresh-button/';
import { useRecentItemsAvailable } from 'hooks/use-recent-items-available';
import { useUserOrders } from 'hooks/use-user-orders';
import AuthRequired from 'pages/authentication/auth-required';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useOrderContext } from 'state/order';
import { routes } from 'utils/routing';

import { DetailsModal } from './details-modal';
import Order from './order';
import OrdersEmpty from './orders-empty';

export enum MODAL_TYPE {
  SHOW_DETAILS = 'showDetails',
  TRACK_ORDER = 'trackOrder',
}

const OrdersContainer = styled.div`
  margin-left: auto;
  margin-right: auto;
  max-width: ${Styles.layout.sectionMaxWidth};
  padding-bottom: 0;
  padding-top: 0;
  width: calc(100% - 2rem);
  & > div {
    padding-top: 0px;
  }
`;

const OrdersEmptyWrapper = styled.div`
  min-height: 70vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const OrdersPanel = styled.div`
  width: 100%;
  ${Styles.desktop`
    padding-top: 2rem;
  `};
`;

const OfferDisclaimer = styled.div`
  text-align: center;
`;

const AccountOrderWrapper = styled.div`
  padding: 1.25rem 0 6.25rem;
  ${Styles.desktop`
    padding-top: 150px;
  `}
`;

const AccountOrders: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const [detailsModal, setDetailsModal] = useState('');
  const [showOrderUnavailableModal, setShowOrderUnavailableModal] = useState(false);
  const { loading, orders, refetch } = useUserOrders(user);
  const { unavailableCartEntries } = useOrderContext();
  const handleDisplayDetailsModal = useCallback(orderId => setDetailsModal(orderId), []);
  const handleOnModalDismiss = useCallback(() => setDetailsModal(''), []);
  const [modalType, setModalType] = useState<string>(MODAL_TYPE.SHOW_DETAILS);
  const { formatMessage } = useIntl();
  const showItems = useRecentItemsAvailable();
  const onRefreshClick = useCallback(() => {
    refetch();
  }, [refetch]);
  const enableRecentItemsAddToCart = useFlag(LaunchDarklyFlag.ENABLE_RECENT_ITEMS_ADD_TO_CART);

  useEffect(() => {
    if (unavailableCartEntries.length) {
      setShowOrderUnavailableModal(true);
    }
  }, [showOrderUnavailableModal, setShowOrderUnavailableModal, unavailableCartEntries.length]);

  const handleModalDismiss = useCallback(() => {
    setShowOrderUnavailableModal(false);
    navigate(routes.cart);
  }, [navigate]);

  const refreshButton = (
    <RefreshButtonContainer>
      <RefreshButton onClick={onRefreshClick}>
        <StyledRefreshIcon />
        {formatMessage({ id: 'refresh' })}
      </RefreshButton>
    </RefreshButtonContainer>
  );
  const hasOrders = !!orders.length;
  return (
    <AccountOrderWrapper>
      <AuthRequired>
        <Helmet title={formatMessage({ id: 'recentOrders' })} />
        {showItems && (
          <OrdersContainer>
            <BackArrowHistory color={Styles.color.headerContrast} />
            {enableRecentItemsAddToCart && (
              <>
                <AccountLayout
                  header={formatMessage({ id: 'recentItems' })}
                  fullWidth
                  mobileContainerPadding="1.25rem 0 0 0"
                >
                  <RecentItems locationName="Recent Orders Page" containerHasTopPadding={false} />
                </AccountLayout>
              </>
            )}
          </OrdersContainer>
        )}

        <AccountLayout
          header={hasOrders ? formatMessage({ id: 'recentOrders' }) : null}
          fullWidth
          mobileContainerPadding="1.25rem 0 0 0"
        >
          {loading ? (
            <LoadingAnimation fillColor={Styles.color.primary} />
          ) : (
            <OrdersContainer>
              {hasOrders ? (
                <>
                  <OfferDisclaimer>
                    {formatMessage({ id: 'recentOrdersDisclaimer' })}
                  </OfferDisclaimer>
                  {refreshButton}
                  <OrdersPanel>
                    {orders.map(order => (
                      <Order
                        key={order.rbiOrderId}
                        order={order}
                        handleDisplayDetailsModal={handleDisplayDetailsModal}
                        setModalType={setModalType}
                      />
                    ))}
                  </OrdersPanel>
                </>
              ) : (
                <OrdersEmptyWrapper>
                  <OrdersEmpty>{refreshButton}</OrdersEmpty>
                </OrdersEmptyWrapper>
              )}
            </OrdersContainer>
          )}
        </AccountLayout>
        {detailsModal && (
          <DetailsModal
            rbiOrderId={detailsModal}
            onModalDismiss={handleOnModalDismiss}
            type={modalType}
            setModalType={setModalType}
          />
        )}
        {showOrderUnavailableModal && (
          <ModalOrderUnavailable onDismiss={handleModalDismiss} items={unavailableCartEntries} />
        )}
      </AuthRequired>
    </AccountOrderWrapper>
  );
};

export default AccountOrders;
